/**
 * Evaluación de filtros contra filas de DB (`filter_attributes` de getmylots /
 * getsharedlist). Espejo de `parcelFilterMatch.js`, que hace lo mismo generando
 * expresiones MapLibre contra props del tile.
 *
 * Se usa con un alcance activo (mis registros / lista) para filtrar los pins a
 * zoom bajo y contar coincidencias exactas, donde los tiles no alcanzan.
 *
 * Null-safety alineada con el motor de tiles: atributo ausente o `null` no
 * matchea (equivalente al `has` de las expresiones MapLibre).
 *
 * @see docs/maps/parcel-filters-scope-backend-spec.md
 */

import { getParcelFilterDefinitions } from './parcelFilterContract';
import { normalizeTextQuery } from './parcelFilterMatch';

/** Clave del atributo en DB: `dbField` explícito, si no el nombre canónico del filtro. */
const dbFieldOf = (def) => def.dbField || def.field;

/**
 * Atributos de la fila. Prioriza los bloques canónicos (`filter_attributes` para
 * la parcela, `private_attributes` para la publicación); cae a las claves
 * canónicas o con prefijo `lot_` que ya trae la raíz del card.
 */
const readAttribute = (row, key) => {
  const attrs = row?.filter_attributes;
  if (attrs && attrs[key] !== undefined) return attrs[key];
  const privateAttrs = row?.private_attributes;
  if (privateAttrs && privateAttrs[key] !== undefined) return privateAttrs[key];
  if (row?.[key] !== undefined) return row[key];
  return row?.[`lot_${key}`];
};

const toNumber = (value) => {
  if (value == null || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/** Minúsculas y sin acentos: DB y tile no siempre escriben igual (p. ej. la provincia). */
const toComparableString = (value) => {
  if (value == null) return null;
  const text = String(value).trim();
  if (text === '') return null;
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
};

/** Variantes aceptadas del valor elegido; espejo de `getMatchValues` en parcelFilterMatch. */
const comparableMatchValues = (def, value) => {
  const option = (def.options || []).find((opt) => opt.value === value);
  const variants = option?.matchValues?.length ? option.matchValues : [value];
  return variants.map(toComparableString).filter(Boolean);
};

const matchesMultiSelect = (def, row, values) => {
  const raw = readAttribute(row, dbFieldOf(def));

  if (def.valueType === 'number') {
    const numeric = toNumber(raw);
    return numeric != null && values.some((value) => toNumber(value) === numeric);
  }

  const text = toComparableString(raw);
  if (text == null) return false;
  return values.some((value) => comparableMatchValues(def, value).includes(text));
};

/** Una banda matchea si el valor cae en su rango (`min` inclusive, `max` según `maxExclusive`). */
const matchesBand = (option, numeric) => {
  if (option.min != null && numeric < option.min) return false;
  if (option.max != null) {
    if (option.maxExclusive ? numeric >= option.max : numeric > option.max) return false;
  }
  return option.min != null || option.max != null;
};

/**
 * Banda serializada en el propio valor (`"min:max"`, `max` vacío = sin techo).
 * La usan los filtros cuyas bandas no están en el contrato porque se calculan
 * sobre los datos, como el precio de una lista compartida.
 */
const parseSerializedBand = (value) => {
  const text = String(value);
  if (!text.includes(':')) return null;
  const [min, max] = text.split(':');
  return { min: toNumber(min), max: toNumber(max), maxExclusive: true };
};

/**
 * Bandas contra valor crudo de DB. Incluye las bucketizadas en tile
 * (`matchMode: 'exact'`), cuyas opciones llevan el `min`/`max` equivalente.
 * Superficie: `urbansurface` y, si falta, `surface`.
 */
const matchesRangeBands = (def, row, values) => {
  let numeric = toNumber(readAttribute(row, dbFieldOf(def)));
  if (numeric == null && def.dbFieldFallback) {
    numeric = toNumber(readAttribute(row, def.dbFieldFallback));
  }
  if (numeric == null) return false;

  const options = values
    .map((value) => (def.options || []).find((option) => option.value === value)
      || parseSerializedBand(value))
    .filter(Boolean);

  return options.some((option) => matchesBand(option, numeric));
};

const matchesTriState = (def, row, value) => {
  const numeric = toNumber(readAttribute(row, dbFieldOf(def)));
  return numeric != null && numeric === value;
};

const matchesInlineBoolean = (def, row) => {
  const numeric = toNumber(readAttribute(row, dbFieldOf(def)));
  if (numeric == null) return false;

  const { op, value } = def.compare || {};
  if (op === '>') return numeric > value;
  if (op === '>=') return numeric >= value;
  return numeric === def.trueValue;
};

/**
 * "Contiene" sobre el campo de DB. Con `normalizeQuery` (calle) se ignoran
 * acentos, igual que contra `street_name_norm` en el tile.
 */
const matchesTextContains = (def, row, query) => {
  const stripAccents = Boolean(def.normalizeQuery);
  const needle = normalizeTextQuery(query, { stripAccents });
  if (needle.length < (def.minLength || 1)) return true;

  const raw = readAttribute(row, dbFieldOf(def));
  if (raw == null) return false;
  return normalizeTextQuery(raw, { stripAccents }).includes(needle);
};

/**
 * ¿La fila cumple un filtro puntual? Exportado para los evaluadores que trabajan
 * sobre otra granularidad, como los filtros de publicación por unidad funcional.
 *
 * @param {object} def definición del contrato
 * @param {object} row fila con los atributos (raíz o bloques canónicos)
 * @param {unknown} value valor del filtro en el panel
 * @returns {boolean} true también cuando el valor no está activo
 */
export const matchesFilter = (def, row, value) => {
  switch (def.component) {
    case 'multiSelect':
      return Array.isArray(value) && value.length > 0
        ? matchesMultiSelect(def, row, value)
        : true;
    case 'rangeBands':
      return Array.isArray(value) && value.length > 0
        ? matchesRangeBands(def, row, value)
        : true;
    case 'triState':
      return value === 0 || value === 1 ? matchesTriState(def, row, value) : true;
    case 'inlineBoolean':
      return value === def.trueValue ? matchesInlineBoolean(def, row) : true;
    case 'textContains':
      return typeof value === 'string' ? matchesTextContains(def, row, value) : true;
    default:
      return true;
  }
};

/**
 * ¿La fila cumple todos los filtros activos evaluables contra DB?
 * Los filtros marcados `scopeUnsupported` se ignoran (la UI los oculta cuando
 * hay alcance activo).
 *
 * @param {object} row fila de getmylots / getsharedlist
 * @param {Record<string, unknown>} filters estado del panel
 * @returns {boolean}
 */
export function matchesParcelFilters(row, filters) {
  if (!row || !filters) return true;

  return getParcelFilterDefinitions()
    .filter((def) => !def.scopeUnsupported)
    .every((def) => {
      const value = filters[def.field];
      return value == null ? true : matchesFilter(def, row, value);
    });
}

/**
 * Valores distintos de un campo de texto en las filas del alcance, para poblar
 * un desplegable sin depender de lo que haya cargado el mapa.
 * @param {Array<object>} rows
 * @param {string} field clave canónica (`readAttribute` resuelve `filter_attributes` / `lot_*`)
 * @returns {string[]} ordenados alfabéticamente
 */
export function getDistinctRowValues(rows, field) {
  if (!Array.isArray(rows) || rows.length === 0) return [];

  const found = new Set();
  rows.forEach((row) => {
    const raw = readAttribute(row, field);
    if (typeof raw !== 'string') return;
    const value = raw.trim();
    if (value.length > 0) found.add(value);
  });

  return Array.from(found).sort((a, b) => a.localeCompare(b, 'es'));
}

/**
 * Filas del alcance que cumplen los filtros activos.
 * @param {Array<object>} rows
 * @param {Record<string, unknown>} filters
 * @returns {Array<object>}
 */
export function filterRowsByParcelFilters(rows, filters) {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  return rows.filter((row) => matchesParcelFilters(row, filters));
}
